import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { notification, Dropdown } from 'antd';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Wallet,
  User,
  CreditCard,
  CircleDollarSign,
  Copy,
  CheckCircle2,
  Star,
  Phone,
  MoreHorizontal,
  Ticket,
} from 'lucide-react';
import { formatDateByDayjs, formatWithThousandSeparator, triggerDownload, formatPhoneDisplay} from '@/utils';
import {
  fetchRemittanceActivityLogByRemittanceUID,
  fetchRemittanceUpdatesCommentByRemittanceUID,
  fetchRemittanceOrders,
} from '@/api/remittanceService';
import { useFetchRemittanceByUID } from '@/hooksApi';
import { usePollingMechanismStore } from '@/stores';
import { ProcessNameEnum } from '@/stores/pollingMechanismStore';
import { useLoadMorePagination, useOpenEntityTicketModal, useOrderListPagination } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import { ActivityItem, PermissionName } from '@/types';
import { TicketEntityIDFieldEnum } from '@/constants';
import { getStatusColors } from '@/pages/order-details/components/StatusColors';

/* ─── sub-components ────────────────────────────────────────────────────── */

interface CardHeaderProps {
  icon: React.ReactNode;
  title: string;
}
function CardHeader({ icon, title }: CardHeaderProps) {
  return (
    <div className='flex items-center gap-2 mb-4'>
      <span className='text-[rgb(115,123,140)]'>{icon}</span>
      <h2 className='text-sm font-bold uppercase tracking-wider text-[rgb(57,57,60)] m-0'>
        {title}
      </h2>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}
function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className='flex items-start justify-between py-2'>
      <span className='text-sm text-[rgb(115,123,140)]'>{label}</span>
      <span className='text-sm font-medium text-[rgb(57,57,60)] text-right'>{value}</span>
    </div>
  );
}

interface StatusBadgeProps {
  slug: string;
  label: string;
}
function StatusBadge({ slug, label }: StatusBadgeProps) {
  const colors = getStatusColors(slug);
  return (
    <span
      className='inline-flex rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap'
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {label}
    </span>
  );
}

interface ActivityTimelineProps {
  items: ActivityItem[];
}
function ActivityTimeline({ items }: ActivityTimelineProps) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <p className='text-sm text-[rgb(115,123,140)] text-center py-4'>
        {String(t('no_activity', { defaultValue: 'No activity' }))}
      </p>
    );
  }
  return (
    <div className='flex flex-col'>
      {items.map((item, index) => (
        <div key={index} className='relative flex gap-3 pb-4 last:pb-0'>
          {index < items.length - 1 && (
            <div className='absolute start-[9px] top-5 w-0.5 h-full bg-[rgb(232,230,227)]' />
          )}
          <CheckCircle2
            className='shrink-0 mt-0.5 w-[18px] h-[18px] z-10 text-[rgb(49,196,127)]'
            strokeWidth={2}
          />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-bold text-[rgb(57,57,60)] m-0 leading-tight'>
              {String(t(item.title))}
            </p>
            {item.description && (
              <p className='text-xs text-[rgb(115,123,140)] m-0 mt-0.5 italic'>
                {item.description}
              </p>
            )}
            <p className='text-[11px] text-[rgb(115,123,140)] m-0 mt-1'>
              {formatDateByDayjs(item.created_at, 'D MMM YYYY, HH:mm')}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

interface TrackingStep {
  label: string;
  date: string;
}
interface TrackingTimelineProps {
  steps: TrackingStep[];
}
function TrackingTimeline({ steps }: TrackingTimelineProps) {
  const { t } = useTranslation();
  if (steps.length === 0) {
    return (
      <p className='text-sm text-[rgb(115,123,140)] text-center py-4'>
        {String(t('no_activity', { defaultValue: 'No activity' }))}
      </p>
    );
  }
  return (
    <div className='flex flex-col'>
      {steps.map((step, index) => (
        <div key={index} className='relative flex gap-3 pb-4 last:pb-0'>
          {index < steps.length - 1 && (
            <div className='absolute start-[9px] top-5 w-0.5 h-full bg-[rgb(232,230,227)]' />
          )}
          <CheckCircle2
            className='shrink-0 mt-0.5 w-[18px] h-[18px] z-10 text-[rgb(49,196,127)]'
            strokeWidth={2}
          />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-bold text-[rgb(57,57,60)] m-0 leading-tight'>{step.label}</p>
            {step.date && (
              <p className='text-[11px] text-[rgb(115,123,140)] m-0 mt-1'>{step.date}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface CommentListProps {
  items: ActivityItem[];
}
function CommentList({ items }: CommentListProps) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <p className='text-sm text-[rgb(115,123,140)] text-center py-4'>
        {String(t('no_updates', { defaultValue: 'No updates' }))}
      </p>
    );
  }
  return (
    <div className='flex flex-col divide-y divide-[rgb(232,230,227)]'>
      {items.map((item, index) => (
        <div key={index} className='flex items-start justify-between gap-4 py-3 first:pt-0'>
          <div className='flex items-start gap-2 min-w-0'>
            <span className='mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[rgb(57,57,60)]' />
            <p className='text-xs text-[rgb(57,57,60)] italic m-0 leading-relaxed'>
              {item.description}
            </p>
          </div>
          <p className='text-xs text-[rgb(115,123,140)] shrink-0 m-0 whitespace-nowrap'>
            {formatDateByDayjs(item.created_at, 'D MMM YYYY')}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ─── page ───────────────────────────────────────────────────────────────── */

const FinanceSingle = () => {
  const [activeSegment, setActiveSegment] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingProof, setIsDownloadingProof] = useState(false);

  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.REMITTANCE);

  const { startPolling } = usePollingMechanismStore();
  const hasExportProcess = usePollingMechanismStore((state) =>
    state.processes.has(ProcessNameEnum.EXPORT_STATEMENT),
  );
  const { data, isLoading } = useFetchRemittanceByUID(id as string);

  const remittanceActivityFetcher = useCallback(
    (page: number) =>
      fetchRemittanceActivityLogByRemittanceUID(id as string, page, 50).then(
        (res) => res.object.items,
      ),
    [id],
  );

  const {
    items: remittanceActivityItems,
    hasMore: hasMoreRemittanceActivity,
    loadingMore: loadingMoreRemittanceActivity,
    loadMore: loadMoreRemittanceActivity,
  } = useLoadMorePagination<ActivityItem>(remittanceActivityFetcher, 50);

  const remittanceUpdatesFetcher = useCallback(
    (page: number) =>
      fetchRemittanceUpdatesCommentByRemittanceUID(id as string, page, 50).then(
        (res) => res.object.items,
      ),
    [id],
  );

  const {
    items: remittanceUpdatesItems,
    hasMore: hasMoreRemittanceUpdates,
    loadingMore: loadingMoreRemittanceUpdates,
    loadMore: loadMoreRemittanceUpdates,
  } = useLoadMorePagination<ActivityItem>(remittanceUpdatesFetcher, 50);

  const response = data?.object;

  /* ── download helpers ── */

  const downloadStatementAsBlob = async (url: string, fileName: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      triggerDownload(blobUrl, fileName);
    } catch {
      triggerDownload(url, fileName);
    }
  };

  const checkRemittanceExport = async () => {
    try {
      const { fetchRemittanceExport } = await import('@/api');
      const exportData = await fetchRemittanceExport(id as string);
      if (exportData.object.status === 'SUCCESS') {
        notification.success({
          message: t('download_ready'),
          description: t('your_file_is_downloading'),
          showProgress: true,
          duration: 5,
        });
        try {
          const { fetchLabelRemittanceByUID } = await import('@/api');
          const blob = await fetchLabelRemittanceByUID(id as string);
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, `statement-${id}.pdf`);
          window.URL.revokeObjectURL(url);
        } catch {
          const file = exportData.object.file;
          await downloadStatementAsBlob(file.abs_url, file.filename || `statement-${id}.pdf`);
        }
        setTimeout(() => setIsDownloading(false), 2000);
        return true;
      }
      if (exportData.object.status === 'FAILED') {
        notification.error({
          message: t('operation_failed_please_try_again'),
          duration: 5,
        });
        setIsDownloading(false);
        return true;
      }
      return false;
    } catch {
      setIsDownloading(false);
      return true;
    }
  };

  const onDownloadStatement = async () => {
    setIsDownloading(true);
    try {
      const { createRemittanceExport } = await import('@/api');
      await createRemittanceExport(id as string);
      startPolling(
        ProcessNameEnum.EXPORT_STATEMENT,
        { fileName: 'statement', toastTextKey: 'preparing_remittance_statement' },
        checkRemittanceExport,
        300000,
      );
    } catch {
      setIsDownloading(false);
    }
  };

  const onDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const { fetchRemittanceExcel } = await import('@/api');
      const blob = await fetchRemittanceExcel(id as string);
      const url = window.URL.createObjectURL(blob);
      triggerDownload(url, `statement-${response?.platform_code || id}.xlsx`);
      window.URL.revokeObjectURL(url);
      notification.success({
        message: t('download_ready'),
        description: t('your_file_is_downloading'),
        duration: 5,
      });
    } catch {
      notification.error({
        message: t('operation_failed_please_try_again'),
        duration: 5,
      });
    }
    setIsDownloading(false);
  };

  const onDownloadProof = async () => {
    setIsDownloadingProof(true);
    try {
      const { fetchRemittanceProof } = await import('@/api/remittanceService');
      const blob = await fetchRemittanceProof(id as string);
      const url = window.URL.createObjectURL(blob);
      triggerDownload(url, `proof-${response?.platform_code || id}.pdf`);
      window.URL.revokeObjectURL(url);
      notification.success({
        message: t('download_ready'),
        description: t('your_file_is_downloading'),
        duration: 5,
      });
    } catch {
      notification.error({
        message: t('operation_failed_please_try_again'),
        duration: 5,
      });
    }
    setIsDownloadingProof(false);
  };

  const isDelivered = response?.status?.slug === 'delivered';

  // Reset download state when polling process completes
  useEffect(() => {
    if (isDownloading && !hasExportProcess) {
      setIsDownloading(false);
    }
  }, [isDownloading, hasExportProcess]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      usePollingMechanismStore.getState().stopPolling(ProcessNameEnum.EXPORT_STATEMENT);
    };
  }, []);

  const handleCopyId = () => {
    if (response?.platform_code) {
      navigator.clipboard.writeText(response.platform_code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  /* ── loading state ── */
  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='h-8 w-8 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
      </div>
    );
  }

  if (!response) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[400px] gap-4'>
        <p className='text-[15px] text-[rgb(115,123,140)]'>
          {t('remittance_not_found', { defaultValue: 'Remittance not found' })}
        </p>
        <button
          type='button'
          onClick={() => navigate('/finance')}
          className='flex items-center gap-2 px-4 py-2 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[13px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
        >
          {t('back_to_finance', { defaultValue: 'Back to Finance' })}
        </button>
      </div>
    );
  }

  const statusSlug = response?.status?.slug ?? '';
  const statusLabel = statusSlug
    ? String(
        t(`merchant_title_order_status.${statusSlug}`, {
          defaultValue: response?.status?.title ?? '',
        }),
      )
    : '';

  const driverName = response?.driver
    ? `${response.driver.first_name || ''} ${response.driver.last_name || ''}`.trim()
    : null;

  const driverInitials = response?.driver
    ? `${(response.driver.first_name ?? '').charAt(0)}${(response.driver.last_name ?? '').charAt(0)}`.toUpperCase()
    : '';

  /* ── load-more button style ── */
  const loadMoreBtn =
    'flex items-center gap-1.5 cursor-pointer border border-solid rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-bold text-[11px] uppercase bg-white border-[rgb(57,57,60)] text-[rgb(57,57,60)]';
  const loadMoreBtnStyle = {
    height: 28,
    padding: '0 10px',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 8,
  };

  /* ── tracking steps from status history (newest first, deduplicated) ── */
  const trackingSteps = (() => {
    const raw = response?.statuses ?? [];
    const seen = new Set<string>();
    // Logical sequence for remittance statuses — used as tiebreaker when timestamps match
    const REMITTANCE_STATUS_ORDER: Record<string, number> = {
      requested: 1,
      initiated: 2,
      'ready-to-pick': 3,
      'in-transit': 4,
      'pending-delivery': 5,
      delivered: 6,
      cancelled: 7,
    };
    return [...raw]
      .sort((a, b) => {
        const ta = new Date(a?.pivot?.created_at || 0).getTime();
        const tb = new Date(b?.pivot?.created_at || 0).getTime();
        if (ta !== tb) return ta - tb; // ascending by time
        // Tiebreaker: logical sequence order
        return (REMITTANCE_STATUS_ORDER[a.slug] ?? 99) - (REMITTANCE_STATUS_ORDER[b.slug] ?? 99);
      })
      .map((s) => ({
        label: String(
          t(`merchant_title_order_status.${s.slug}`, { defaultValue: s.title ?? s.slug }),
        ),
        date: s.pivot?.created_at ? formatDateByDayjs(s.pivot.created_at, 'D MMM YYYY, HH:mm') : '',
        key: `${s.slug}-${s.pivot?.created_at ?? ''}`,
      }))
      .filter((step) => {
        if (seen.has(step.key)) return false;
        seen.add(step.key);
        return true;
      })
      .reverse();
  })();

  const remittanceMenuItems = [
    {
      key: 'excel',
      label: (
        <span className='flex items-center gap-2 text-[13px]'>
          <FileSpreadsheet size={14} />
          {String(t('download_excel', { defaultValue: 'Download Excel' }))}
        </span>
      ),
      onClick: onDownloadExcel,
    },
    {
      key: 'pdf',
      label: (
        <span className='flex items-center gap-2 text-[13px]'>
          <FileText size={14} />
          {String(t('download_pdf_statement', { defaultValue: 'Download PDF' }))}
        </span>
      ),
      onClick: onDownloadStatement,
    },
    ...(isDelivered
      ? [
          {
            key: 'proof',
            label: (
              <span className='flex items-center gap-2 text-[13px]'>
                <Download size={14} />
                {String(t('download_proof', { defaultValue: 'Download Proof' }))}
              </span>
            ),
            onClick: onDownloadProof,
          },
        ]
      : []),
  ];

  return (
    <div className='relative flex flex-col pb-4'>
      {/* ── sub-header (ID + date + status badge) ── */}
      <div className='flex items-start justify-between mb-5 px-1'>
        <div>
          <div className='flex items-center gap-2'>
            <h2 className='text-xl font-bold tracking-tight text-[rgb(57,57,60)] m-0'>
              {response?.platform_code ?? id ?? '—'}
            </h2>
            {response?.platform_code && (
              <button
                onClick={handleCopyId}
                className='p-1 rounded-md hover:bg-[rgb(245,245,243)] transition-colors cursor-pointer'
                title={copied ? String(t('copied')) : String(t('copy'))}
              >
                <Copy className='h-4 w-4 text-[rgb(115,123,140)]' />
              </button>
            )}
          </div>
          {response?.created_at && (
            <span className='text-[12px] text-[rgb(115,123,140)]'>
              {formatDateByDayjs(response.created_at, 'D MMM YYYY, HH:mm')}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {statusSlug && <StatusBadge slug={statusSlug} label={statusLabel} />}
          {hasPermission(PermissionName.CREATE_TICKET) && (
            <button
              onClick={() => openEntityTicketModal(response?.platform_code)}
              className='flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[11px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              <Ticket size={13} />
              {String(t('ticket'))}
            </button>
          )}
          <Dropdown
            menu={{ items: remittanceMenuItems }}
            trigger={['click']}
            placement='bottomRight'
          >
            <button
              type='button'
              className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            >
              <MoreHorizontal size={16} />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Download actions moved to FinanceDetailPageHeader — removed inline duplicate (Bug #46) */}

      {/* ── segment tabs ── */}
      <div className='flex w-full border-b border-[rgb(232,230,227)] mb-5'>
        <button
          onClick={() => setActiveSegment(0)}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${
            activeSegment === 0
              ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
              : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
          }`}
        >
          {String(t('details'))}
        </button>
        <button
          onClick={() => setActiveSegment(1)}
          className={`px-5 pb-2.5 pt-1 text-[13px] font-semibold border-0 border-b-2 border-solid -mb-px transition-colors cursor-pointer bg-transparent ${
            activeSegment === 1
              ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
              : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
          }`}
        >
          {String(t('orders'))}
        </button>
      </div>

      {/* ── Details tab content ── */}
      {activeSegment === 0 && (
        <div className='grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start mb-24'>
          {/* ── LEFT column ── */}
          <div className='flex flex-col gap-5'>
            {/* 2-col sub-grid: Remittance Info + Driver */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
              {/* Remittance Info card */}
              <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
                <CardHeader
                  icon={<Wallet className='h-4 w-4' />}
                  title={String(t('remittance_info', { defaultValue: 'Remittance Info' }))}
                />
                <div className='mb-4'>
                  <p className='text-[11px] text-[rgb(115,123,140)] uppercase tracking-wider m-0 mb-1'>
                    {String(t('amount'))}
                  </p>
                  <div className='flex items-baseline gap-1.5'>
                    <span className='text-[24px] font-bold text-[rgb(57,57,60)] leading-none'>
                      {formatWithThousandSeparator(response?.amount ?? 0)}
                    </span>
                    <span className='text-sm text-[rgb(115,123,140)]'>{String(t('iqd'))}</span>
                  </div>
                </div>
                <div className='divide-y divide-[rgb(232,230,227)]'>
                  {response?.created_at && (
                    <InfoRow
                      label={String(t('date'))}
                      value={formatDateByDayjs(response.created_at, 'D MMM YYYY, HH:mm')}
                    />
                  )}
                  {(response?.orders_count ?? response?.statuses?.length) ? (
                    <InfoRow
                      label={String(t('orders'))}
                      value={String(response?.orders_count ?? response?.statuses?.length ?? 0)}
                    />
                  ) : null}
                </div>
              </section>

              {/* Driver card */}
              <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
                <CardHeader icon={<User className='h-4 w-4' />} title={String(t('driver'))} />
                {driverName ? (
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className='w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[rgb(245,245,243)]'>
                        <span className='text-[13px] font-semibold text-[rgb(57,57,60)]'>
                          {driverInitials}
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-[14px] font-semibold text-[rgb(57,57,60)]'>
                          {driverName}
                        </span>
                        {response?.driver?.phone && (
                          <span className='text-[12px] text-[rgb(115,123,140)]' dir='ltr'>
                            {formatPhoneDisplay(response.driver.phone)}
                          </span>
                        )}
                      </div>
                    </div>
                    {response?.driver?.phone && (
                      <a
                        href={`tel:${response.driver.phone}`}
                        className='w-9 h-9 rounded-full bg-white border border-[rgb(232,230,227)] flex items-center justify-center shrink-0 hover:bg-[rgb(245,245,243)] transition-colors'
                      >
                        <Phone className='w-4 h-4 text-[rgb(57,57,60)]' />
                      </a>
                    )}
                  </div>
                ) : (
                  <p className='text-sm text-[rgb(115,123,140)]'>
                    {String(t('no_driver_assigned', { defaultValue: 'No driver assigned' }))}
                  </p>
                )}
              </section>
            </div>

            {/* Payment Details card (full width) */}
            <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
              <CardHeader
                icon={<CreditCard className='h-4 w-4' />}
                title={String(t('payment_details', { defaultValue: 'Payment Details' }))}
              />
              <div className='divide-y divide-[rgb(232,230,227)]'>
                <InfoRow
                  label={String(t('payment_method'))}
                  value={response?.payment_method ?? String(t('cash'))}
                />
                {response?.paid_to && (
                  <InfoRow
                    label={String(t('paid_to', { defaultValue: 'Paid to' }))}
                    value={response.paid_to}
                  />
                )}
                <InfoRow
                  label={String(t('total_cod_collected'))}
                  value={`${formatWithThousandSeparator(response?.amount ?? 0)} ${String(t('iqd'))}`}
                />
              </div>
            </section>

            {/* Comment updates card */}
            <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
              <CardHeader
                icon={<FileText className='h-4 w-4' />}
                title={String(t('remittance_updates', { defaultValue: 'Remittance Updates' }))}
              />
              <CommentList items={remittanceUpdatesItems} />
              {hasMoreRemittanceUpdates && (
                <div className='flex justify-center mt-4'>
                  <button
                    onClick={loadMoreRemittanceUpdates}
                    disabled={loadingMoreRemittanceUpdates}
                    className={loadMoreBtn}
                    style={loadMoreBtnStyle}
                  >
                    {loadingMoreRemittanceUpdates ? (
                      <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                    ) : (
                      <ChevronDown className='h-3 w-3' strokeWidth={2} />
                    )}
                    {String(t('load_more'))}
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* ── RIGHT column ── */}
          <div className='flex flex-col gap-5 xl:sticky xl:top-5'>
            {/* Cost Summary card */}
            <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
              <CardHeader
                icon={<CircleDollarSign className='h-4 w-4' />}
                title={String(t('cost_summary', { defaultValue: 'Cost Summary' }))}
              />
              <div className='divide-y divide-[rgb(232,230,227)]'>
                <div className='flex items-center justify-between py-2'>
                  <span className='text-sm text-[rgb(115,123,140)]'>
                    {String(t('total_cod_collected'))}
                  </span>
                  <span className='text-sm font-medium text-[rgb(57,57,60)]'>
                    {formatWithThousandSeparator(response?.amount ?? 0)} {String(t('iqd'))}
                  </span>
                </div>
                <div className='flex items-center justify-between py-2.5'>
                  <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                    {String(t('total'))}
                  </span>
                  <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                    {formatWithThousandSeparator(response?.amount ?? 0)} {String(t('iqd'))}
                  </span>
                </div>
              </div>
            </section>

            {/* Payment Summary card */}
            <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
              <CardHeader
                icon={<CreditCard className='h-4 w-4' />}
                title={String(t('payment_summary', { defaultValue: 'Payment Summary' }))}
              />
              <div className='divide-y divide-[rgb(232,230,227)]'>
                <InfoRow
                  label={String(t('payment_method'))}
                  value={response?.payment_method ?? String(t('cash'))}
                />
                {response?.paid_to && (
                  <InfoRow
                    label={String(t('paid_to', { defaultValue: 'Paid to' }))}
                    value={response.paid_to}
                  />
                )}
                <div className='flex items-center justify-between py-2.5'>
                  <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                    {String(t('total_paid', { defaultValue: 'Total Paid' }))}
                  </span>
                  <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                    {formatWithThousandSeparator(response?.amount ?? 0)} {String(t('iqd'))}
                  </span>
                </div>
              </div>
            </section>

            {/* Tracking Timeline card */}
            <section className='rounded-2xl border border-[rgb(232,230,227)] bg-white p-5'>
              <CardHeader
                icon={<CheckCircle2 className='h-4 w-4' />}
                title={String(t('tracking_timeline', { defaultValue: 'Tracking Timeline' }))}
              />
              {trackingSteps.length > 0 ? (
                <TrackingTimeline steps={trackingSteps} />
              ) : remittanceActivityItems.length > 0 ? (
                <ActivityTimeline items={[...remittanceActivityItems].reverse()} />
              ) : (
                <p className='text-sm text-[rgb(115,123,140)] text-center py-4'>
                  {String(t('no_activity', { defaultValue: 'No activity' }))}
                </p>
              )}
              {hasMoreRemittanceActivity && (
                <div className='flex justify-center mt-4'>
                  <button
                    onClick={loadMoreRemittanceActivity}
                    disabled={loadingMoreRemittanceActivity}
                    className={loadMoreBtn}
                    style={loadMoreBtnStyle}
                  >
                    {loadingMoreRemittanceActivity ? (
                      <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                    ) : (
                      <ChevronDown className='h-3 w-3' strokeWidth={2} />
                    )}
                    {String(t('load_more'))}
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      )}

      {/* ── Orders tab ── */}
      {activeSegment === 1 && <RemittanceOrdersList remittanceUid={id as string} />}
    </div>
  );
};

/* ─── Remittance Orders List ─── */
const REMITTANCE_ORDERS_PAGE_SIZE = 25;

function RemittanceOrdersList({ remittanceUid }: { remittanceUid: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Server-side load-more (25/batch) — same as pickup/RTO. The backend paginates
  // the merged (direct + claim) order set with a stable id-desc order.
  const fetcher = useCallback(
    async (page: number) => {
      const res = await fetchRemittanceOrders(remittanceUid, page, REMITTANCE_ORDERS_PAGE_SIZE);
      const obj = res?.object ?? {};
      const items = Array.isArray(obj) ? obj : (obj.items ?? []);
      return {
        items,
        page: obj.page ?? page,
        total: obj.total ?? items.length,
        pages: obj.pages ?? 1,
      };
    },
    [remittanceUid],
  );

  const {
    items: orders,
    total,
    isLoading: loading,
    loadingMore,
    hasMore,
    loadMore,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useOrderListPagination<any>(fetcher);

  const getStatusColor = (slug: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      delivered: { bg: 'hsla(152,60%,48%,0.12)', text: 'hsl(152,60%,38%)' },
      'partially-delivered': { bg: 'hsla(140,50%,48%,0.12)', text: 'hsl(140,45%,38%)' },
      'rto-delivered': { bg: 'hsla(340,50%,52%,0.12)', text: 'hsl(340,45%,42%)' },
      'rto-partially-delivered': { bg: 'hsla(330,45%,52%,0.12)', text: 'hsl(330,40%,42%)' },
      'in-transit': { bg: 'hsla(195,70%,50%,0.12)', text: 'hsl(195,65%,40%)' },
    };
    return colors[slug] ?? { bg: 'hsla(0,0%,50%,0.10)', text: 'hsl(0,0%,40%)' };
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-10'>
        <div className='w-6 h-6 border-2 border-[rgb(237,224,74)] border-t-transparent rounded-full animate-spin' />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] p-8 text-center mb-24'>
        <span className='text-sm text-[rgb(115,123,140)]'>
          {String(t('no_orders_in_remittance', { defaultValue: 'No orders in this remittance' }))}
        </span>
      </div>
    );
  }

  return (
    <div className='mb-24'>
      <div className='flex items-center justify-between mb-3'>
        <span className='text-[12px] text-[rgb(115,123,140)]'>
          {total} {String(t('orders'))}
        </span>
      </div>
      <div className='bg-white rounded-2xl border border-[rgb(232,230,227)] overflow-hidden'>
        <div className='grid grid-cols-[1fr_120px_1fr_120px] gap-4 px-5 py-3 border-b border-[rgb(232,230,227)]'>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('order'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('status'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('customer'))}
          </span>
          <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)] text-end'>
            {String(t('amount'))}
          </span>
        </div>
        {orders.map((order: any) => {
          const statusSlug = order.status?.slug || '';
          const statusColor = getStatusColor(statusSlug);
          return (
            <button
              key={order.uid}
              type='button'
              onClick={() => navigate(`/orders/${order.uid}`)}
              className='grid grid-cols-[1fr_120px_1fr_120px] gap-4 px-5 py-3.5 text-start cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors w-full bg-transparent border-0'
              style={{ borderBottom: '1px solid rgb(232,230,227)' }}
            >
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: 'rgb(57,57,60)',
                }}
              >
                {order.platform_code}
              </span>
              <div className='flex items-center'>
                <span
                  className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap'
                  style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                >
                  {order.status?.merchant_title || statusSlug}
                </span>
              </div>
              <span className='text-[13px] text-[rgb(57,57,60)]'>{order.full_name || '—'}</span>
              <span className='text-[13px] font-medium text-[rgb(57,57,60)] text-end'>
                {formatWithThousandSeparator(order.products_value || order.fee || 0)}{' '}
                {String(t('iqd'))}
              </span>
            </button>
          );
        })}
      </div>

      {hasMore ? (
        <div className='mt-4 flex justify-center'>
          <Button
            variant='default'
            onClick={loadMore}
            disabled={loadingMore}
            className='gap-2 px-5 font-semibold shadow-sm'
          >
            {loadingMore ? (
              <span className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
            ) : (
              <ChevronDown className='h-4 w-4' />
            )}
            {String(t('load_more', { defaultValue: 'Load more' }))}
            <span className='text-xs font-normal opacity-70'>
              {orders.length}/{total}
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default FinanceSingle;
